// src/components/recruitment/CreateHiringRequisitionModal.jsx
// ============================================================
// Enterprise Reusable "Create Hiring Requisition" Modal
// Used in both Team Lead and Admin Panels.
// Styled identically to the HR "Create New Job Opening" modal:
// 650px width, clean 2-column grid layout, standard EMS typography,
// inputs, dropdowns, textareas, date pickers, and green theme.
// ============================================================

import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { createHiringRequisition } from "../../hooks/useHiringRequisitions";
import { EMS_DEPARTMENTS } from "../../utils/hrConstants";
import { toast } from "react-hot-toast";
import { Plus, X, Briefcase } from "lucide-react";

export default function CreateHiringRequisitionModal({ isOpen, onClose, onSuccess }) {
  const { userProfile } = useAuth();

  const roleRaw = (userProfile?.role || "").toLowerCase();
  const userRole = roleRaw.includes("admin") ? "Admin" : "Team Lead";
  const userName = userProfile?.displayName || userProfile?.name || userRole;
  const userDept = userProfile?.department || "Engineering";

  const [formData, setFormData] = useState({
    title: "",
    department: EMS_DEPARTMENTS.includes(userDept) ? userDept : "Engineering",
    vacancies: 1,
    employmentType: "Full-Time",
    experienceRequired: "2-4 years",
    priority: "Medium",
    salaryRange: "8 - 12 LPA",
    deadline: "",
    skillsRequired: "",
    justification: "",
  });

  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Please enter a position title");
      return;
    }
    setSubmitting(true);

    try {
      const skillsArr = typeof formData.skillsRequired === "string"
        ? formData.skillsRequired.split(",").map((s) => s.trim()).filter(Boolean)
        : formData.skillsRequired;

      await createHiringRequisition({
        title: formData.title.trim(),
        positionTitle: formData.title.trim(),
        department: formData.department,
        openings: Number(formData.vacancies) || 1,
        vacancies: Number(formData.vacancies) || 1,
        employmentType: formData.employmentType,
        experience: formData.experienceRequired,
        experienceRequired: formData.experienceRequired,
        priority: formData.priority,
        salaryRange: formData.salaryRange,
        expectedJoiningDate: formData.deadline,
        deadline: formData.deadline,
        skills: skillsArr,
        skillsRequired: skillsArr,
        justification: formData.justification.trim(),
        description: formData.justification.trim(),
        requestedById: userProfile?.uid || "",
        createdById: userProfile?.uid || "",
        requestedByName: userName,
        createdByName: userName,
        requestedByRole: userRole,
        createdRole: userRole,
        requestedDate: new Date().toISOString(),
        status: "Submitted",
      });

      toast.success(`Hiring Requisition for "${formData.title}" submitted to HR!`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error("Failed to submit requisition: " + err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1.25rem;
          box-sizing: border-box;
        }
        .modal-body {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 650px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 1.75rem 2rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 1.1rem;
          border-bottom: 1px solid #f1f5f9;
          margin-bottom: 1.25rem;
        }
        .modal-title-group { display: flex; align-items: center; gap: 0.75rem; }
        .modal-title-icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: #e6f4ed; color: #1b4b31;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .modal-title { margin: 0; font-size: 1.15rem; font-weight: 700; color: #0f172a; }
        .modal-subtitle { margin: 3px 0 0 0; font-size: 0.78rem; color: #64748b; }
        .modal-close-btn {
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 50%;
          border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b;
          cursor: pointer; transition: all 0.15s ease; padding: 0;
        }
        .modal-close-btn:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }

        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.1rem; }
        @media (max-width: 600px) { .form-grid-2 { grid-template-columns: 1fr; } }

        .form-field { display: flex; flex-direction: column; gap: 0.4rem; }
        .form-label { font-size: 0.8rem; font-weight: 600; color: #334155; display: flex; align-items: center; gap: 0.25rem; }
        .form-label .required { color: #ef4444; }

        .modal-footer { display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #f1f5f9; }

        .btn-secondary {
          background: #fff; color: #334155;
          border: 1.5px solid #e2e8f0; border-radius: 9px;
          padding: 0.55rem 1.15rem; font-weight: 600; font-size: 0.86rem;
          cursor: pointer; transition: all 0.15s ease;
        }
        .btn-secondary:hover { background: #f8fafc; border-color: #cbd5e1; }

        .btn-primary {
          height: 42px; padding: 0 1.25rem;
          background: linear-gradient(135deg, #1b4b31 0%, #065f46 100%);
          color: #fff; border: none; border-radius: 10px;
          font-weight: 700; font-size: 0.85rem; cursor: pointer;
          display: inline-flex; align-items: center; gap: 0.5rem;
          box-shadow: 0 4px 12px rgba(27, 75, 49, 0.2);
          transition: all 0.2s ease;
        }
        .btn-primary:hover {
          background: linear-gradient(135deg, #143724 0%, #044e39 100%);
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(27, 75, 49, 0.3);
        }

        .modal-body .ats-input,
        .modal-body .ats-select,
        .modal-body textarea.ats-input {
          width: 100%; box-sizing: border-box; padding: 0.6rem 0.85rem;
          border: 1.5px solid #cbd5e1; border-radius: 9px;
          font-size: 0.86rem; font-weight: 500; background: #ffffff; color: #0f172a;
          outline: none; transition: all 0.2s ease;
        }
        .modal-body .ats-input:focus,
        .modal-body .ats-select:focus,
        .modal-body textarea.ats-input:focus {
          border-color: #1b4b31; box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }
      `}</style>

      <div className="modal-body" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-title-group">
            <div className="modal-title-icon">
              <Briefcase size={20} />
            </div>
            <div>
              <h3 className="modal-title">Create Hiring Requisition</h3>
              <p className="modal-subtitle">
                Submitted by <strong>{userName}</strong> ({userRole}) · Pending HR Approval
              </p>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Row 1: Title & Department */}
          <div className="form-grid-2">
            <div className="form-field">
              <label className="form-label">
                Position Title <span className="required">*</span>
              </label>
              <input
                type="text"
                className="ats-input"
                required
                placeholder="e.g. Senior Frontend Engineer"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label className="form-label">
                Department <span className="required">*</span>
              </label>
              <select
                className="ats-select"
                required
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              >
                <option value="">Select Department</option>
                {EMS_DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Vacancies & Employment Type */}
          <div className="form-grid-2">
            <div className="form-field">
              <label className="form-label">
                Vacancies / Openings <span className="required">*</span>
              </label>
              <input
                type="number"
                min="1"
                className="ats-input"
                required
                value={formData.vacancies}
                onChange={(e) => setFormData({ ...formData, vacancies: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label className="form-label">Employment Type</label>
              <select
                className="ats-select"
                value={formData.employmentType}
                onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
              >
                <option value="Full-Time">Full-Time</option>
                <option value="Part-Time">Part-Time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
          </div>

          {/* Row 3: Experience Required & Priority */}
          <div className="form-grid-2">
            <div className="form-field">
              <label className="form-label">Experience Required</label>
              <input
                type="text"
                className="ats-input"
                placeholder="e.g. 2-4 years"
                value={formData.experienceRequired}
                onChange={(e) => setFormData({ ...formData, experienceRequired: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label className="form-label">Priority Level</label>
              <select
                className="ats-select"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Row 4: Salary Range & Target Date */}
          <div className="form-grid-2">
            <div className="form-field">
              <label className="form-label">Salary Range / Budget</label>
              <input
                type="text"
                className="ats-input"
                placeholder="e.g. 8 - 12 LPA"
                value={formData.salaryRange}
                onChange={(e) => setFormData({ ...formData, salaryRange: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label className="form-label">Target Joining Date</label>
              <input
                type="date"
                className="ats-input"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              />
            </div>
          </div>

          {/* Row 5: Required Skills */}
          <div className="form-field">
            <label className="form-label">Required Skills (comma separated)</label>
            <input
              type="text"
              className="ats-input"
              placeholder="e.g. React, Node.js, TypeScript, SQL"
              value={formData.skillsRequired}
              onChange={(e) => setFormData({ ...formData, skillsRequired: e.target.value })}
            />
          </div>

          {/* Row 6: Business Justification */}
          <div className="form-field">
            <label className="form-label">Business Justification & Requirements</label>
            <textarea
              className="ats-input"
              style={{ height: 80, resize: "vertical" }}
              placeholder="Describe headcount rationale, key responsibilities, or project allocation..."
              value={formData.justification}
              onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
            />
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              <Plus size={16} /> {submitting ? "Submitting..." : "Submit Hiring Requisition"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
